// Stage Editor-2:BlogDraftEditor(共用タイトル + WYSIWYG 本文エディタ)。
//
// 役割:
// - title input + body を 1 つの controlled 値で持つ
// - 3 tab:
//     1) 編集   — WYSIWYG(TipTap)Rich Text Editor(実際の見た目で編集)
//     2) Code   — HTML ソース表示 + 編集(monospace textarea)
//     3) プレビュー — 編集と同じ RichTextContent を editable=false で描画。
//                  toolbar は表示しない。技術スタック(TipTap + StarterKit +
//                  Underline + Link)と CSS / sanitize 経路が **完全に同じ**。
// - 3 tab で同じ body 文字列を共有(単一データ源)。
// - 文字数 / 保存ステータス / 最終保存時刻をフッターに表示。
// - 編集 tab 用の共通 toolbar を内部で描画(
//   `BlogDraftEditorToolbar`)。この toolbar は Edit tab の TipTap
//   editor instance を操作する。
//
// 非役割:
// - API を呼ばない(SAVE は親コンポーネントが value + onChange で行う)
// - AI / WordPress / ai-web-engine などの業務知識を持たない
// - sessionStorage / localStorage を持たない(親が管理)
//
// 安全:
// - Rich Text エディタ出力を TipTap の getHTML() で取得
// - Code tab 経由で外部 HTML 注入 → setContent 前段で DOMPurify を通過(
//   Edit / Preview 両方の path で実施)
// - <script> / <iframe> / on* / javascript: は TipTap StarterKit に無く、
//   Link extension は javascript: scheme を拒否。

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Image as TiptapImage } from "@tiptap/extension-image";
import DOMPurify from "dompurify";
import type { Config as DOMPurifyConfig } from "dompurify";

import { BlogDraftEditorToolbar } from "./BlogDraftEditorToolbar";
import { uploadMedia } from "../../modules/media/api/mediaApi";

// ---- 型 ----

export type BlogDraftEditorValue = {
  title: string;
  body: string;
};

export type BlogDraftEditorMode = "create" | "edit";

export type BlogDraftEditorSaveStatus =
  | "idle"
  | "dirty"
  | "saving"
  | "saved"
  | "error";

export interface BlogDraftEditorProps {
  /** 現在の値(controlled)。title + body をまとめて持つ */
  value: BlogDraftEditorValue;
  /** 値が変わった時に呼ばれる。title / body の片方だけ変わる時も完全な object を渡す */
  onChange: (next: BlogDraftEditorValue) => void;

  /** "create" / "edit"。今は表示上の違いはないが、Mode で将来分岐する余地として持つ */
  mode?: BlogDraftEditorMode;
  /** true なら title / body 編集をすべて禁止(loading / streaming 中など) */
  readOnly?: boolean;

  /** true なら UI ヒントを出す(タブ disabled 等)。MVP はヘッダ表示のみ */
  isStreaming?: boolean;

  /** 保存ステータス。フッター右に表示 */
  saveStatus?: BlogDraftEditorSaveStatus;
  /** 最終保存日時(ISO8601)。saveStatus="saved" の時に表示 */
  lastSavedAt?: string | null;

  /** 必須バッジ表示。true で field-label-required が出る */
  titleRequired?: boolean;
  bodyRequired?: boolean;

  /** 文字数上限。超えると maxLength で入力を止める */
  maxTitleLength?: number;
  maxBodyLength?: number;

  /** 追加 class。`.blog-draft-editor--large` で本文 textarea / WYSIWYG を高くする */
  className?: string;
  /** タイトル入力欄の右側に置く任意 UI。 */
  titleAction?: ReactNode;
  /** タイトル入力欄の直下に差し込む任意 UI。AI Blog のタイトル候補などで使用。 */
  titleAddon?: ReactNode;
}

// ---- 内部定数 ----

const SAVE_STATUS_LABEL: Record<BlogDraftEditorSaveStatus, string> = {
  idle: "未編集",
  dirty: "未保存",
  saving: "保存中...",
  saved: "保存済み",
  error: "保存失敗",
};

type TabKey = "edit" | "code" | "preview";

// TipTap 共有 extensions — Edit / Preview 両方の RichTextContent で
// 同一の配列を使う(= 完全に同じ技術スタック)。
//
// Stage Editor-3 (2026-09-15):Image extension を追加。
//   - drag & drop / paste で画像ファイルを挿入可能にするため
//   - allowBase64: false(画像は必ず /api/media/upload 経由で WP 保存)
//   - WordPress 保存後の source_url を src に使うため base64 fallback は不要
//
// Stage Editor-3.1 (2026-09-15):width / height を schema に追加。
//   @tiptap/extension-image の addAttributes は src / alt / title の 3
//   個しか持たない。toolbar の「画像幅」select から updateAttributes
//   で width を渡せるよう、自前で width / height attrs を schema に
//   足し、renderHTML で <img width="500" height="333"> の形で出力。
const ImageWithSize = TiptapImage.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: {
        default: null,
        parseHTML: (element: HTMLElement) => parseWidthAttr(element),
        // 2026-09-16 方針 B: width は **常に inline style** で出力する。
        //   - HTML <img width="..."> 属性は W3C 仕様上 **integer pixel のみ**
        //     受理("300px" / "100%" は invalid → DOMPurify / wp_kses に
        //     よって attribute ごと剥ぐ)
        //   - inline style の width: は px / % どちらも受理されるため、
        //     px と % の両方を sanitize 経由でも 100% 保つには style に
        //     統一するのが一番安全
        //   - renderHTML は `widthToRenderAttrs` 経由で
        //       "300px"  → style="width:300px;"
        //       "100%"   → style="width:100%;"
        //     を出力。HTML attribute としての width は一切出さない
        //   - parseHTML は style の width: を最優先、HTML width 属性は
        //     レガシー互換のための fallback
        renderHTML: (attrs: { width: string | null }) =>
          widthToRenderAttrs(attrs.width),
      },
      height: {
        default: null,
        parseHTML: (element: HTMLElement) =>
          parseBlogImageLengthAttr(element.getAttribute("height")),
        renderHTML: (attrs: { height: string | null }) =>
          attrs.height ? { height: attrs.height } : {},
      },
      // Stage Editor-3.2 (2026-09-15):align attr を追加。
      //   left / center / right の 3 値 + null(デフォルト)。
      //   renderHTML は inline style で出力(DOMPurify が style を
      //   通す前提。FORBID_ATTR から style を削除した)。
      //   WordPress は inline style を尊重するので <img style="...">
      //   で float / margin を渡せば表示される。
      align: {
        default: null,
        parseHTML: (element: HTMLElement) => parseAlignAttr(element),
        renderHTML: (attrs: { align: "left" | "center" | "right" | null }) => {
          if (attrs.align === "left")
            return {
              style:
                "float: left; margin-right: 1em; margin-bottom: 0.5em; max-width: 100%;",
            };
          if (attrs.align === "right")
            return {
              style:
                "float: right; margin-left: 1em; margin-bottom: 0.5em; max-width: 100%;",
            };
          if (attrs.align === "center")
            return { style: "display: block; margin-left: auto; margin-right: auto;" };
          return {};
        },
      },
    };
  },

  // 2026-09-16:parseHTML() override。
  //   parent (@tiptap/extension-image) の parseHTML() は
  //   `{tag: "img[src]:not([src^=\"data:\"])"}` を返すだけで **getAttrs が無い**。
  //   Tiptap core が後段で addAttributes().parseHTML を注入するため、
  //   ここでは parent と同じ tag rule だけを返し、属性解析は
  //   width / height / align の parseHTML に集約する。
  parseHTML() {
    return [
      {
        tag: 'img[src]:not([src^="data:"])',
      },
    ];
  },
});

// ----------------------------------------------------------------
// parseHTML 用の純粋関数(JSX 外 + parseHTML rule の中から呼ぶ)
// ----------------------------------------------------------------

// ----------------------------------------------------------------
// 画像 width の内部表現と HTML 出力/入力の境界
// ----------------------------------------------------------------
// 設計(2026-09-16 方針 B):
//   Editor 内部の attrs.width は **常に単位付き** で保存する:
//     - "300px" / "500px" / "800px"          ← pixel preset
//     - "100%" / "50%"                       ← パーセント preset
//     - "auto" / "inherit"                  ← 将来用
//   renderHTML は **必ず inline style** として出力する(HTML attribute は使わない):
//     - "300px"  → style="width:300px;"
//     - "100%"   → style="width:100%;"
//     - null     → 属性を出さない
//   parseHTML は逆変換。style を最優先、レガシー HTML width 属性は fallback:
//     - style="width:300px;"  → "300px"
//     - style="width:100%;"   → "100%"
//     - width="300"           → "300px"  (旧 integer pixel)
//     - width="300px"         → "300px"  (旧 invalid attribute — sanitize 後
//                                         残ることは無いが、防御的に対応)
//
// なぜ style に統一するか(2026-09-16 結論):
//   HTML 仕様上 <img width="..."> は **非負整数のみ**(W3C HTML Living Standard)。
//   "300px" / "100%" は invalid → DOMPurify / wp_kses 等の正規 sanitizer は
//   attribute ごと剥ぐ。px と % を両方 sanitize 経路で保つには、style に
//   統一するのが唯一無二の解。
// ----------------------------------------------------------------

export function parseBlogImageLengthAttr(raw: string | null): string | null {
  // 内部表現への正規化。X は整数 / 小数を受け付ける。
  //   - "300px" / "300 px" / "300"  → "300px"
  //   - "100%" / "50%"              → "100%" (単位そのまま保持)
  //   - "500em" / "500rem"          → "500em"
  //   - 無い / 空                   → null
  //   - "auto" / "inherit"          → raw そのまま(将来用、未対応)
  if (!raw) return null;
  const trimmed = raw.trim().toLowerCase();
  if (!trimmed) return null;
  const m = trimmed.match(/^(\d+(?:\.\d+)?)(px|em|rem|%)?$/);
  if (!m) return raw; // auto / inherit 等 — そのまま渡す
  const num = m[1];
  const unit = m[2] || "px"; // unit 省略時は px とみなす
  return `${num}${unit}`;
}

/**
 * 内部表現の width → HTML の inline style 属性への変換。
 * renderHTML から呼ばれる。**必ず style に統一**して出力するため、
 * HTML <img width="..."> 属性は出さない。
 *   - "300px"  → { style: "width:300px;" }
 *   - "100%"   → { style: "width:100%;" }
 *   - null     → {}
 *
 * 注: align の inline style とマージする必要があるため、caller 側で
 * 必ず align.renderHTML 結果と Object.assign してから DOM に渡す。
 * (TipTap の renderHTML は mergeAttributes で結合されるので安全。)
 */
function widthToRenderAttrs(width: string | null): Record<string, string> {
  const result: Record<string, string> = !width ? {} : { style: `width:${width};` };
  return result;
}

/**
 * HTML の inline style / width attribute → 内部表現の width に変換。
 * parseHTML から呼ばれる。
 *   - style="width:300px;" → "300px"
 *   - style="width:100%;"  → "100%"
 *   - width="300"          → "300px"   (legacy integer pixel)
 *   - width="300px"        → "300px"   (legacy / direct DOM injection)
 *   - 両方無い              → null
 *
 * **style を最優先** する。HTML width attribute は legacy 互換 fallback。
 */
function parseWidthAttr(element: HTMLElement): string | null {
  // 1. inline style の width:… を最優先で読む
  //    (現在の出力形式 = 常に style 経由なので、こちらが正)
  const style = element.getAttribute("style") || "";
  const styleMatch = style.match(/(?:^|;)\s*width\s*:\s*([^;]+)/i);
  if (styleMatch) {
    const fromStyle = parseBlogImageLengthAttr(styleMatch[1]);
    if (fromStyle) return fromStyle;
  }
  // 2. HTML width attribute は legacy fallback
  //    (旧 editor / 旧 backend / 手書き HTML 救済)
  //    ※ 2026-09-16 方針 B では本来不要だが、WP に古い `<img width="N">`
  //    が残っている時の救済のため残す。
  return parseBlogImageLengthAttr(element.getAttribute("width"));
}

function parseAlignAttr(element: HTMLElement): "left" | "center" | "right" | null {
  // 2026-09-16:正則と renderHTML 出力を一致させる
  //   - center: renderHTML は `margin-left:auto; margin-right:auto` を
  //     別プロパティで出す。旧 regex の `margin: ...auto` 簡記はヒット
  //     しないため toolbar dropdown が「配置」に戻る UX バグがあった。
  //   - WP の class 互換:alignleft/right/center を class に乗る WP テーマ
  //     もある(class を先に評価)
  if (element.classList.contains("alignleft")) return "left";
  if (element.classList.contains("alignright")) return "right";
  if (element.classList.contains("aligncenter")) return "center";
  const s = element.getAttribute("style") || "";
  if (/float\s*:\s*left/i.test(s)) return "left";
  if (/float\s*:\s*right/i.test(s)) return "right";
  if (
    /display\s*:\s*block/i.test(s) &&
    (
      /margin\s*:\s*[^;]*auto/i.test(s) ||
      (/margin-left\s*:\s*auto/i.test(s) &&
        /margin-right\s*:\s*auto/i.test(s))
    )
  )
    return "center";
  return null;
}

const SHARED_EXTENSIONS = [
  StarterKit.configure({}),
  Underline,
  Link.configure({
    openOnClick: false,
    autolink: true,
  }),
  ImageWithSize.configure({
    inline: false,
    allowBase64: false,
    HTMLAttributes: { loading: "lazy" },
  }),
] as const;

// DOMPurify — Code tab 経由の外部 HTML 注入に対して
// Edit / Preview の両 path で setContent 前に sanitize する。
const PURIFY_CONFIG: DOMPurifyConfig = {
  ALLOWED_TAGS: [
    "p", "br",
    "h1", "h2", "h3", "h4", "h5", "h6",
    "ul", "ol", "li",
    "strong", "b", "em", "i", "u", "s",
    "a",
    "blockquote", "q", "cite",
    "pre", "code",
    "hr",
    "img",
    "table", "thead", "tbody", "tfoot", "tr", "th", "td",
  ],
  ALLOWED_ATTR: [
    "href", "title",
    "src", "alt", "width", "height", "loading", "style", "class",
    "scope", "colspan", "rowspan",
    "border",
  ],
  ALLOWED_URI_REGEXP:
    /^(?:(?:https?|mailto|tel):|tel:\+|\/|#|[^a-zA-Z0-9+\-./])/i,
  FORBID_TAGS: [
    "script", "style", "iframe", "object", "embed",
    "form", "input", "link", "meta", "base",
    "frame", "frameset", "applet", "noscript",
  ],
  FORBID_ATTR: [
    "onload", "onerror", "onclick", "onmouseover", "onfocus",
    "onblur", "onchange", "onsubmit",
  ],
  RETURN_DOM_FRAGMENT: false,
  RETURN_DOM: false,
};

function sanitizeBody(body: string): string {
  if (!body || typeof body !== "string") return "";
  const safe = DOMPurify.sanitize(body, PURIFY_CONFIG) as unknown;
  return typeof safe === "string" ? safe : "";
}

// ---- ヘルパー ----

function formatJst(iso: string): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const fmt = new Intl.DateTimeFormat("en-CA", {
      timeZone: "Asia/Tokyo",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", hour12: false,
    });
    const parts = fmt.formatToParts(d).reduce<Record<string, string>>(
      (acc, p) => {
        acc[p.type] = p.value;
        return acc;
      },
      {}
    );
    return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
  } catch {
    return iso;
  }
}

function cx(...names: Array<string | false | null | undefined>): string {
  return names.filter(Boolean).join(" ");
}

// ---- 画像クライアント側 resize ----
//
// 目的:
//   - ドラッグで入れた 4K / iPhone 写真 / スクリーンショットを
//     最大辺 1200px に縮小して upload
//   - JPEG / PNG / WebP / GIF に対応(GIF は静止画として 1 フレーム)
//   - 透過 PNG は PNG 出力で保持、それ以外は JPEG quality=0.92
//   - 既に小さい画像はそのまま返す(double resize 抑止)
//
// 失敗時:例外を投げず元ファイルを返す(caller がそのまま upload を試みる)。
async function resizeImageBeforeUpload(file: File): Promise<File> {
  // 画像以外は触らない(GIF アニメは 1 フレーム化されるが、最低限画像として扱う)
  if (!file.type.startsWith("image/")) return file;
  // SVG / ICO などは canvas が扱えないのでスキップ
  if (file.type === "image/svg+xml" || file.type === "image/x-icon") return file;

  const MAX_DIMENSION = 1200; // WordPress medium_large サイズ相当

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error("FileReader failed"));
    reader.readAsDataURL(file);
  });

  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("image decode failed"));
    el.src = dataUrl;
  });

  const w = img.naturalWidth;
  const h = img.naturalHeight;
  if (!w || !h) return file;

  // 既に十分小さい → そのまま返す(再 encode による劣化を避ける)
  if (w <= MAX_DIMENSION && h <= MAX_DIMENSION) return file;

  const scale = Math.min(MAX_DIMENSION / w, MAX_DIMENSION / h, 1);
  const targetW = Math.round(w * scale);
  const targetH = Math.round(h * scale);

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return file;
  ctx.drawImage(img, 0, 0, targetW, targetH);

  // 出力形式:PNG 入力は PNG(透過保持)、それ以外は JPEG
  const isPng = file.type === "image/png";
  const blob = await new Promise<Blob | null>((resolve) => {
    if (isPng) {
      canvas.toBlob((b) => resolve(b), "image/png");
    } else {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92);
    }
  });
  if (!blob) return file;

  // 拡張子 + mime を元ファイル名に合わせる
  const dotIdx = file.name.lastIndexOf(".");
  const base = dotIdx > 0 ? file.name.slice(0, dotIdx) : file.name;
  const ext = isPng ? "png" : "jpg";
  const mimeType = isPng ? "image/png" : "image/jpeg";
  const resized = new File([blob], `${base}.${ext}`, {
    type: mimeType,
    lastModified: Date.now(),
  });
  return resized;
}

// ---- 本体 ----

export const BlogDraftEditor = ({
  value,
  onChange,
  mode = "create",
  readOnly = false,
  isStreaming = false,
  saveStatus = "idle",
  lastSavedAt = null,
  titleRequired = true,
  bodyRequired = true,
  maxTitleLength = 200,
  maxBodyLength = 50000,
  className,
  titleAction = null,
  titleAddon = null,
}: BlogDraftEditorProps) => {
  const [activeTab, setActiveTab] = useState<TabKey>("edit");

  // 単一データ源:body(HTML string)。3 tab すべてが同じ body を読み書きする。
  // Edit / Preview は TipTap(Rich Text renderer)で表示し、Code は textarea で
  // HTML ソースを表示。ContentElement に同じ CSS / 同一 extensions を適用する
  // ため、編集と Preview は視覚的に同じレンダリング結果を得る。
  const editIsEditable = !readOnly && !isStreaming;

  // TipTap の onUpdate ループ抑止:
  //   setContent 直後の emit が走ると onUpdate が再発火するため、
  //   ref で 1 回スキップする。
  const isInternalUpdateRef = useRef(false);

  const lastCommittedHtmlRef = useRef<string>(sanitizeBody(value.body));

  // Stage Editor-3:画像アップロード関数(ref 経由)。
  // editorProps.handlePaste / handleDrop から呼ばれるため、
  // useEditor の外で定義 + ref 経由で最新 editor にアクセスする。
  // テナント一貫性:uploadMedia は内部で /api/media/upload を叩き、
  // backend が g.current_user["tenant_id"] で tenant の WordPress 接続を使う。
  // フロント側で tenant_id を扱う必要は無い(JWT セッションが保証)。
  //
  // 2026-09-15 追記:upload 前にクライアント側 resize。
  //   - 元画像が 4K / iPhone HEIC のまま WP に飛ぶのを抑止
  //   - resize 後の自然な寸法で WP 表示 → editor 上で width/height
  //     を手で触らなくても「それなりの大きさ」になる
  //   - resize 拡張(Option C)のドラッグ revert 問題を緩和(
  //     width attr を後から書き換える必要が無くなる)
  const editorRef = useRef<Editor | null>(null);
  const uploadAndInsertImage = useRef(async (file: File) => {
    const ed = editorRef.current;
    if (!ed) return;
    try {
      const resized = await resizeImageBeforeUpload(file);
      const item = await uploadMedia({
        file: resized,
        title: file.name,
        alt_text: file.name,
      });
      if (item.source_url) {
        ed.chain().focus().setImage({
          src: item.source_url,
          alt: file.name,
        }).run();
      }
    } catch (err) {
      // 最小実装:console + window.alert のみ。
      // UX 改善(進捗表示 / toast / retry)は将来課題。
      const msg = err instanceof Error ? err.message : String(err);
      // eslint-disable-next-line no-console
      console.error("[BlogDraftEditor] image upload failed:", err);
      window.alert(`画像のアップロードに失敗しました: ${msg}`);
    }
  });

  // 単一 Rich Text editor instance — Edit tab の TipTap。
  // Preview tab は同じ RichTextContent コンポーネントを editable=false
  // でもう 1 個 useEditor する(= 2 インスタンスだが同一 extensions /
  // 同一 CSS / 同一 sanitize 経路)。これは React + TipTap の構造上
  // 不可避(EditorContent を 1 度 DOM に置いたら別 tab に移せない)。
  const editor: Editor | null = useEditor(
    {
      extensions: [...SHARED_EXTENSIONS],
      content: sanitizeBody(value.body),
      editable: editIsEditable,
      // Stage Editor-3:drag & drop / paste で画像をアップロード → 挿入。
      // - handleDrop: 外部ファイルドロップを ProseMirror が拾う前に乗っ取り
      // - handlePaste: clipboard の画像ファイルを乗っ取り
      // どちらも image/* のみ拾い、それ以外は false を返して ProseMirror の
      // デフォルト挙動(text / html paste)に任せる。
      editorProps: {
        handleDrop(_view, event, _slice, moved) {
          if (moved) return false;
          const files = event.dataTransfer?.files;
          if (!files || files.length === 0) return false;
          const file = files[0];
          if (!file.type.startsWith("image/")) return false;
          event.preventDefault();
          void uploadAndInsertImage.current(file);
          return true;
        },
        handlePaste(_view, event) {
          const items = event.clipboardData?.items;
          if (!items) return false;
          for (let i = 0; i < items.length; i++) {
            const item = items[i];
            if (item.type.startsWith("image/")) {
              const file = item.getAsFile();
              if (file) {
                event.preventDefault();
                void uploadAndInsertImage.current(file);
                return true;
              }
            }
          }
          return false;
        },
      },
      onUpdate: ({ editor: ed }) => {
        if (isInternalUpdateRef.current) {
          isInternalUpdateRef.current = false;
          return;
        }
        const next = ed.getHTML();
        if (next === lastCommittedHtmlRef.current) return;
        lastCommittedHtmlRef.current = next;
        onChange({ title: value.title, body: next });
      },
    },
    [editIsEditable]
  );

  // editorRef 同期:uploadAndInsertImage から最新の editor にアクセスするため。
  useEffect(() => {
    editorRef.current = editor;
  }, [editor]);

  // Code textarea 経由、または AI 生成 → 親 body 更新 → editor sync
//
// 比对ルール(2026-09-16 修正):
//   - 写入时 sanitize(XSS 对策)
//   - 比对时**不**sanitize(否则 ProseMirror 输出 + DOMPurify 标准化的
//     形式不一致,会误判每次键入都需要 setContent → selection 重置)
//   - 修正前:每次 sanitize 都会让 lastCommittedHtmlRef 与 editor.getHTML()
//     永久 mismatch,AI 生成之后任何手动键入都会触发 setContent
  useEffect(() => {
    if (!editor) return;
    const raw = value.body || "";
    if (raw === lastCommittedHtmlRef.current) return;
    try {
      if (editor.getHTML() === raw) {
        lastCommittedHtmlRef.current = raw;
        return;
      }
    } catch {
      // editor.getHTML() が一瞬失敗することがある。setContent だけ試行。
    }
    isInternalUpdateRef.current = true;
    editor.commands.setContent(sanitizeBody(raw) || "", false);
    lastCommittedHtmlRef.current = raw;
  }, [value.body, editor]);

  const setTitle = useCallback(
    (nextTitle: string) => {
      onChange({ title: nextTitle, body: value.body });
    },
    [onChange, value.body]
  );

  const setBodyFromCode = useCallback(
    (nextBody: string) => {
      onChange({ title: value.title, body: nextBody });
    },
    [onChange, value.title]
  );

  const titleLen = value.title.length;
  const bodyLen = value.body.length;
  const statusLabel = SAVE_STATUS_LABEL[saveStatus];
  const showLastSaved = saveStatus === "saved" && !!lastSavedAt;

  const onCodeChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setBodyFromCode(e.target.value);
  };

  const tabBtnDisabled = readOnly || isStreaming;

  return (
    <div className={cx("blog-draft-editor", className)} data-mode={mode}>
      {/* タイトル */}
      <div className="field">
        <label className="field-label">
          タイトル
          {titleRequired && <span className="field-label-required">必須</span>}
          <span className="blog-draft-editor-counter">
            {titleLen} / {maxTitleLength}
          </span>
        </label>
        <div className="blog-draft-title-input-row">
          <input
            className="input"
            type="text"
            value={value.title}
            disabled={readOnly}
            maxLength={maxTitleLength}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="記事のタイトルを入力してください"
            aria-label="タイトル"
          />
          {titleAction}
        </div>
        {titleAddon}
      </div>

      {/* Tabs */}
      <div className="blog-draft-editor-tabs" role="tablist" aria-label="編集ビュー">
        {(["edit", "code", "preview"] as const).map((t) => {
          const active = activeTab === t;
          const label = t === "edit" ? "編集" : t === "code" ? "Code" : "プレビュー";
          return (
            <button
              key={t}
              type="button"
              role="tab"
              aria-selected={active}
              className={cx("blog-draft-editor-tab", active && "active")}
              onClick={() => setActiveTab(t)}
              disabled={tabBtnDisabled && t !== "preview" /* preview 切替は streaming 中でも許す */}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* 編集 tab — Toolbar + TipTap EditorContent
          (Edit 専用 Rich Text Renderer) */}
      {activeTab === "edit" && (
        <div className="blog-draft-editor-edit-pane">
          <BlogDraftEditorToolbar editor={editor} />
          <div
            className={cx(
              "blog-draft-editor-rich",
              !editIsEditable && "blog-draft-editor-rich--readonly"
            )}
            data-placeholder={value.body ? undefined : "本文を入力してください"}
          >
            <EditorContent editor={editor} />
          </div>
        </div>
      )}

      {/* Code tab — HTML ソース textarea */}
      {activeTab === "code" && (
        <textarea
          className={cx("input", "blog-draft-editor-textarea", "blog-draft-editor-textarea--code")}
          value={value.body}
          disabled={readOnly}
          maxLength={maxBodyLength}
          rows={24}
          onChange={onCodeChange}
          placeholder="<p>...</p>"
          aria-label="本文(ソース)"
          spellCheck={false}
        />
      )}

      {/* Preview tab — 同じ TipTap の Render 経路を editable=false で描画。
          Edit tab の toolbar は別 DOM ノード、Preview では toolbar を出さない
          (= 「toolbar hidden」要件)。
          Content 自体は TipTap + 同一 extensions + 同一 CSS で描画され、
          Edit tab の本文と同じレンダリング結果を得る。 */}
      {activeTab === "preview" && (
        <PreviewPane html={value.body} />
      )}

      {/* 必須バッジ本文横表示 */}
      <div className="blog-draft-editor-required-line">
        本文
        {bodyRequired && <span className="field-label-required">必須</span>}
      </div>

      {/* フッター(文字数 + 保存状態) */}
      <div className="blog-draft-editor-footer">
        <span className="blog-draft-editor-count">
          文字数: <strong>{bodyLen.toLocaleString("ja-JP")}</strong> /{" "}
          {maxBodyLength.toLocaleString("ja-JP")}
        </span>
        <span className="blog-draft-editor-status" data-status={saveStatus}>
          {statusLabel}
          {showLastSaved && (
            <span className="blog-draft-editor-last-saved">
              (最終保存 {formatJst(lastSavedAt ?? "")})
            </span>
          )}
        </span>
      </div>
    </div>
  );
};

// ---- PreviewPane: 同じ TipTap extensions + 同一 CSS で editable=false 描画 -----

function PreviewPane({ html }: { html: string }) {
  // 同じ SHARED_EXTENSIONS + 同一 DOMPurify 経路 + 同一 .blog-draft-editor-rich
  // クラスを経由して表示する。Edit tab と完全同一の Rich Text Renderer 構造。
  const sanitized = useMemo(() => sanitizeBody(html), [html]);
  const isInternalUpdateRef = useRef(false);
  const lastCommittedHtmlRef = useRef<string>(sanitized);

  const editor: Editor | null = useEditor(
    {
      extensions: [...SHARED_EXTENSIONS],
      content: sanitized,
      editable: false,
      onUpdate: () => {
        // editable=false なので本来ここに来ない。万一のループ回避。
      },
    },
    [false]
  );

  // body(親 state)更新時に同期:Edit/Code からの変更を preview に反映するため。
  useEffect(() => {
    if (!editor) return;
    const incoming = sanitized;
    if (incoming === lastCommittedHtmlRef.current) return;
    try {
      if (editor.getHTML() === incoming) {
        lastCommittedHtmlRef.current = incoming;
        return;
      }
    } catch {
      // editor.getHTML() が editor 一時未初期化で失敗することがある
    }
    isInternalUpdateRef.current = true;
    editor.commands.setContent(incoming || "", false);
    lastCommittedHtmlRef.current = incoming;
  }, [sanitized, editor]);

  return (
    <div
      className="blog-draft-editor-rich"
      aria-label="プレビュー"
    >
      <EditorContent editor={editor} />
    </div>
  );
}
